import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import { ExpandableCard } from "shared/ui/ComposedCards";
import EmptyState from "shared/ui/EmptyState";
import { OperationHistoryDialog } from "features/investments";
import { useTranslation } from "shared/hooks/useTranslation";
import { useMarketQuotesContext, useMarketQuotePanels, useRateLimitRemaining } from "features/market/context/MarketQuotesContext";
import { useFinancialVisibility } from "shared/contexts/UserContext";
import {
    getTickerForCurrency,
    type CurrencyVariant,
    type Quote,
} from "features/market/lib/quotes";
import type { QuoteCurrency } from "features/market/lib/marketQuotes";
import { PANEL_TYPES, type PanelType } from "features/market/api/MarketQuotes";
import { formatCell, getTableCellSx, truncateSx } from "features/investments";
import type { Operation } from "types/Portfolio";

const DUAL_CURRENCY_PANELS: PanelType[] = ["acciones", "cedears"];

const tooltipPopperSlotProps = {
    popper: {
        sx: { maxWidth: 280, zIndex: 1600 },
    },
};

export type AddPositionParams = {
    panel: PanelType;
    symbol: string;
    name?: string;
    defaultPrice?: number;
};

interface DisplayRow {
    quote: Quote;
    variant: CurrencyVariant;
    panel: PanelType;
}

type SortKey = "symbol" | "name" | "lastTraded" | "dailyVariation" | "volumeTraded";
type SortDirection = "asc" | "desc";

function compareText(a: string, b: string): number {
    return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function compareNullableNumber(a: number | null, b: number | null): number {
    const aNull = a == null;
    const bNull = b == null;
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    return a - b;
}

function formatPrice(value: number | null): string {
    return value == null ? "-" : `$ ${formatCell(value)}`;
}

function variationColor(value: number | null): string {
    if (value == null) return "text.secondary";
    if (value > 0) return "success.main";
    if (value < 0) return "error.main";
    return "text.secondary";
}

function variationLabel(value: number | null): string {
    if (value == null) return "-";
    return `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function getLanguageLocale(language: string): string {
    return language.startsWith("es") ? "es-AR" : "en-US";
}

function formatUpdatedAt(value: string | undefined, language: string): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat(getLanguageLocale(language), {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
}

function CellWithTooltip({
    value,
    children,
    placement = "top",
}: {
    value: string;
    children: React.ReactNode;
    placement?: "top" | "bottom";
}) {
    const content = (
        <Box component="span" sx={truncateSx} display="block">
            {children}
        </Box>
    );
    if (!value || value === "-") return content;
    return (
        <Tooltip title={value} placement={placement} enterDelay={400} slotProps={tooltipPopperSlotProps} disableInteractive>
            {content}
        </Tooltip>
    );
}

function ChangeCell({ value, sx }: { value: string | number | null; sx: object }) {
    if (value == null || typeof value === "string") {
        return <TableCell align="right" sx={sx}>-</TableCell>;
    }
    const display = variationLabel(value);
    return (
        <TableCell align="right" sx={{ ...sx, color: variationColor(value), fontWeight: 850, ...truncateSx }}>
            <Tooltip title={display} placement="top" enterDelay={400} slotProps={tooltipPopperSlotProps} disableInteractive>
                <Box component="span" sx={truncateSx} display="block">
                    {display}
                </Box>
            </Tooltip>
        </TableCell>
    );
}

const PANEL_MAX_ROWS = 8;

function PanelCompactQuotesTable({
    displayRows,
    t,
}: {
    displayRows: DisplayRow[];
    t: (key: string, options?: Record<string, unknown>) => string;
}) {
    const rows = displayRows.slice(0, PANEL_MAX_ROWS);
    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 800, py: 0.75 }}>{t("investments.symbol")}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, py: 0.75 }}>{t("investments.lastTradedShort")}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, py: 0.75 }}>{t("investments.dailyVariationShort")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={`${row.variant.ticker}-${index}`} hover>
                            <TableCell sx={{ fontWeight: 750, py: 0.65 }}>{row.variant.ticker}</TableCell>
                            <TableCell align="right" sx={{ py: 0.65, fontVariantNumeric: "tabular-nums" }}>
                                {formatPrice(row.variant.lastPrice)}
                            </TableCell>
                            <ChangeCell value={row.variant.variation} sx={{ py: 0.65 }} />
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function QuotesTable({
    displayRows,
    onAddPosition,
    onOpenHistory,
    t,
}: {
    displayRows: DisplayRow[];
    onAddPosition?: (params: AddPositionParams) => void;
    onOpenHistory: (row: DisplayRow) => void;
    t: (key: string, options?: Record<string, unknown>) => string;
}) {
    const theme = useTheme();
    const compactHeaders = useMediaQuery(theme.breakpoints.down("md"));
    const { cellSx: baseCellSx, headerCellSx } = getTableCellSx();
    const [sortKey, setSortKey] = useState<SortKey>("symbol");
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
    const canAddPosition = Boolean(onAddPosition);

    const quotesHeaderSx = {
        ...headerCellSx,
        whiteSpace: "normal" as const,
        wordBreak: "break-word" as const,
        lineHeight: 1.25,
        verticalAlign: "bottom" as const,
        py: 1,
    };
    const numericCellSx = {
        ...baseCellSx,
        whiteSpace: "nowrap" as const,
        minWidth: 88,
    };
    const symbolCellSx = { ...baseCellSx, ...truncateSx, maxWidth: { xs: 76, sm: 96 } };
    const nameCellSx = { ...baseCellSx, ...truncateSx, maxWidth: { xs: 140, sm: 200, md: 260 } };

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
            return;
        }
        setSortKey(key);
        setSortDirection("desc");
    };

    const sortedRows = useMemo(() => {
        const rows = [...displayRows];
        rows.sort((left, right) => {
            let compare = 0;
            switch (sortKey) {
                case "symbol":
                    compare = compareText(left.variant.ticker, right.variant.ticker);
                    break;
                case "name":
                    compare = compareText(left.quote.name, right.quote.name);
                    break;
                case "lastTraded":
                    compare = compareNullableNumber(left.variant.lastPrice, right.variant.lastPrice);
                    break;
                case "dailyVariation":
                    compare = compareNullableNumber(left.variant.variation, right.variant.variation);
                    break;
                case "volumeTraded":
                    compare = compareNullableNumber(left.variant.volume, right.variant.volume);
                    break;
            }
            return sortDirection === "asc" ? compare : -compare;
        });
        return rows;
    }, [displayRows, sortDirection, sortKey]);

    const addPosition = (row: DisplayRow) => {
        if (!onAddPosition) return;
        onAddPosition({
            panel: row.panel,
            symbol: row.variant.ticker,
            name: row.quote.name,
            defaultPrice: row.variant.lastPrice ?? undefined,
        });
    };

    return (
        <TableContainer sx={{ mt: 1, maxHeight: 480, width: "100%", overflow: "auto", WebkitOverflowScrolling: "touch" }}>
            <Table
                size="small"
                stickyHeader
                sx={{
                    minWidth: { xs: 520, sm: 720 },
                    width: "100%",
                    tableLayout: "auto",
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ ...quotesHeaderSx, minWidth: { xs: 56, sm: 104 }, whiteSpace: "nowrap" }}>
                            <TableSortLabel
                                active={sortKey === "symbol"}
                                direction={sortKey === "symbol" ? sortDirection : "asc"}
                                onClick={() => handleSort("symbol")}
                                sx={{ whiteSpace: "nowrap", flexWrap: "nowrap" }}
                            >
                                <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>{t("investments.symbolShort")}</Box>
                                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{t("investments.symbol")}</Box>
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ ...quotesHeaderSx, minWidth: 120, display: { xs: "none", sm: "table-cell" }, whiteSpace: "nowrap" }}>
                            <TableSortLabel
                                active={sortKey === "name"}
                                direction={sortKey === "name" ? sortDirection : "asc"}
                                onClick={() => handleSort("name")}
                                sx={{ whiteSpace: "nowrap", flexWrap: "nowrap" }}
                            >
                                {t("investments.name")}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ ...quotesHeaderSx, minWidth: 96 }} align="right">
                            <TableSortLabel
                                active={sortKey === "lastTraded"}
                                direction={sortKey === "lastTraded" ? sortDirection : "asc"}
                                onClick={() => handleSort("lastTraded")}
                                sx={{ width: "100%", justifyContent: "flex-end", whiteSpace: "normal" }}
                            >
                                {compactHeaders ? t("investments.lastTradedShort") : t("investments.lastTraded")}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ ...quotesHeaderSx, minWidth: 88 }} align="right">
                            <TableSortLabel
                                active={sortKey === "dailyVariation"}
                                direction={sortKey === "dailyVariation" ? sortDirection : "asc"}
                                onClick={() => handleSort("dailyVariation")}
                                sx={{ width: "100%", justifyContent: "flex-end", whiteSpace: "normal" }}
                            >
                                {compactHeaders ? t("investments.dailyVariationShort") : t("investments.dailyVariation")}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ ...quotesHeaderSx, minWidth: 100, display: { xs: "none", md: "table-cell" } }} align="right">
                            <TableSortLabel
                                active={sortKey === "volumeTraded"}
                                direction={sortKey === "volumeTraded" ? sortDirection : "asc"}
                                onClick={() => handleSort("volumeTraded")}
                                sx={{ width: "100%", justifyContent: "flex-end", whiteSpace: "normal" }}
                            >
                                {compactHeaders ? t("investments.volumeTradedShort") : t("investments.volumeTraded")}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ ...quotesHeaderSx, minWidth: 52, width: 56 }} align="right">
                            <Typography component="span" variant="inherit" sx={{ fontWeight: 700, fontSize: "inherit" }}>
                                {compactHeaders ? t("investments.actionsShort") : t("common.actions")}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedRows.map((row, index) => (
                        <TableRow
                            key={`${row.variant.ticker}-${index}`}
                            hover={canAddPosition}
                            onClick={() => addPosition(row)}
                            sx={{ cursor: canAddPosition ? "pointer" : "default" }}
                            role={canAddPosition ? "button" : undefined}
                            tabIndex={canAddPosition ? 0 : undefined}
                            onKeyDown={(event) => {
                                if (!canAddPosition) return;
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    addPosition(row);
                                }
                            }}
                        >
                            <TableCell sx={{ ...symbolCellSx, fontWeight: 700 }}>
                                <CellWithTooltip value={row.variant.ticker}>
                                    {row.variant.ticker}
                                </CellWithTooltip>
                            </TableCell>
                            <TableCell sx={{ ...nameCellSx, display: { xs: "none", sm: "table-cell" } }}>
                                <CellWithTooltip value={row.quote.name}>
                                    {row.quote.name}
                                </CellWithTooltip>
                            </TableCell>
                            <TableCell sx={numericCellSx} align="right">
                                <CellWithTooltip value={formatCell(row.variant.lastPrice)}>
                                    {formatCell(row.variant.lastPrice)}
                                </CellWithTooltip>
                            </TableCell>
                            <ChangeCell value={row.variant.variation} sx={numericCellSx} />
                            <TableCell sx={{ ...nameCellSx, display: { xs: "none", md: "table-cell" } }}>
                                <CellWithTooltip value={formatCell(row.variant.volume)}>
                                    {formatCell(row.variant.volume)}
                                </CellWithTooltip>
                            </TableCell>
                            <TableCell sx={numericCellSx} align="right">
                                <IconButton
                                    size="small"
                                    aria-label={t("portfolio.operationsForAsset", { symbol: row.variant.ticker })}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onOpenHistory(row);
                                    }}
                                >
                                    <HistoryIcon fontSize="small" />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default function MarketQuotesCard({
    onAddPosition,
    operations = [],
    selectedPanel: selectedPanelProp,
    onSelectedPanelChange,
    density = "page",
    viewFullMarketHref,
}: {
    onAddPosition?: (params: AddPositionParams) => void;
    operations?: Operation[];
    selectedPanel?: PanelType;
    onSelectedPanelChange?: (panel: PanelType) => void;
    density?: "page" | "panel";
    viewFullMarketHref?: string;
}) {
    const { t, currentLanguage } = useTranslation();
    const isPanel = density === "panel";
    const { balancesVisible } = useFinancialVisibility();
    const [internalSelectedPanel, setInternalSelectedPanel] = useState<PanelType>("acciones");
    const [historyTarget, setHistoryTarget] = useState<DisplayRow | null>(null);
    const {
        refreshQuotes,
        requestLoad,
        quoteCurrencyView,
        setQuoteCurrencyView,
    } = useMarketQuotesContext();
    const rateLimitRemaining = useRateLimitRemaining();

    const selectedPanel = selectedPanelProp ?? internalSelectedPanel;
    const quotePanels = useMarketQuotePanels([selectedPanel]);
    const panelState = quotePanels[selectedPanel];
    const setSelectedPanel = onSelectedPanelChange ?? setInternalSelectedPanel;
    const isDualCurrency = DUAL_CURRENCY_PANELS.includes(selectedPanel);

    useEffect(() => {
        requestLoad(selectedPanel);
    }, [selectedPanel, requestLoad]);

    const { displayRows, loading: isLoading, err, updatedAt } = useMemo(() => {
        const panelData = panelState?.data ?? null;
        const load = panelState?.loading ?? false;
        const errMsg = panelState?.error ?? null;
        const quotes = panelData?.quotes ?? [];

        const rows: DisplayRow[] = [];
        for (const quote of quotes) {
            const variant = getTickerForCurrency(quote, quoteCurrencyView);
            if (variant) {
                rows.push({ quote, variant, panel: selectedPanel });
            }
        }
        rows.sort((a, b) =>
            a.variant.ticker.toLowerCase().localeCompare(b.variant.ticker.toLowerCase()),
        );

        return { displayRows: rows, loading: load, err: errMsg, updatedAt: panelData?.updatedAt };
    }, [panelState, selectedPanel, quoteCurrencyView]);

    const topMovers = useMemo(() => {
        return displayRows
            .filter((row) => row.variant.variation != null && row.variant.variation !== 0)
            .sort((a, b) => Math.abs(b.variant.variation ?? 0) - Math.abs(a.variant.variation ?? 0))
            .slice(0, 5);
    }, [displayRows]);

    const operationsForTicker = useMemo(() => {
        if (!historyTarget) return [];
        const symbol = historyTarget.variant.ticker;
        const panel = historyTarget.panel;
        return operations.filter((operation) => {
            const asset = operation.assetId;
            return (
                asset?.type === "market" &&
                asset.symbol === symbol &&
                asset.marketPanel === panel
            );
        });
    }, [historyTarget, operations]);

    const handleRefresh = () => {
        if (rateLimitRemaining > 0) return;
        refreshQuotes(selectedPanel);
    };

    const panelSelector = (
        <Box
            role="tablist"
            sx={{
                mt: isPanel ? 1 : 1.75,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
            }}
        >
            {PANEL_TYPES.map((panel) => {
                const selected = selectedPanel === panel;
                return (
                    <Button
                        key={panel}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => setSelectedPanel(panel)}
                        sx={{
                            flex: isPanel ? "1 1 calc(50% - 6px)" : { xs: "1 1 calc(50% - 6px)", sm: "0 0 auto" },
                            minWidth: isPanel ? 0 : { xs: 0, sm: 96 },
                            minHeight: isPanel ? 34 : 38,
                            px: 1.15,
                            borderRadius: 999,
                            textTransform: "none",
                            fontSize: isPanel ? 11 : 13,
                            fontWeight: 900,
                            whiteSpace: "nowrap",
                            color: selected ? "primary.contrastText" : "text.secondary",
                            bgcolor: selected ? "primary.main" : "transparent",
                            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, selected ? 0 : 0.2)}`,
                            boxShadow: selected
                                ? (theme) => `0 10px 22px ${alpha(theme.palette.primary.main, 0.23)}`
                                : "none",
                            "&:hover": {
                                bgcolor: (theme) => selected
                                    ? theme.palette.primary.main
                                    : alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.1 : 0.06),
                            },
                        }}
                    >
                        {t(`investments.${panel}`)}
                    </Button>
                );
            })}
        </Box>
    );

    if (isPanel) {
        return (
            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    p: 1.75,
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                        {t("investments.featuredAssets")}
                    </Typography>
                    <Tooltip
                        title={rateLimitRemaining > 0
                            ? `${t("investments.refreshQuotesWait")} (${rateLimitRemaining}s)`
                            : t("investments.refreshQuotes")}
                    >
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleRefresh}
                                disabled={rateLimitRemaining > 0}
                                aria-label={t("investments.refreshQuotes")}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>

                {panelSelector}

                {isLoading ? (
                    <QuotesLoadingState />
                ) : err ? (
                    <EmptyState icon={<ShowChartRoundedIcon />} title={t("investments.stocksError")} body={err} />
                ) : displayRows.length === 0 ? (
                    <EmptyState
                        icon={<ShowChartRoundedIcon />}
                        title={t("investments.noQuotesTitle")}
                        body={t("investments.noQuotesBody")}
                    />
                ) : (
                    <Box sx={{ mt: 1 }}>
                        <PanelCompactQuotesTable displayRows={displayRows} t={t} />
                        {displayRows.length > PANEL_MAX_ROWS && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, px: 0.5 }}>
                                {t("secondaryPanel.moreQuotes", { count: displayRows.length - PANEL_MAX_ROWS })}
                            </Typography>
                        )}
                    </Box>
                )}

                {viewFullMarketHref && (
                    <Button
                        component={RouterLink}
                        to={viewFullMarketHref}
                        size="small"
                        sx={{ mt: 1.25, textTransform: "none", fontWeight: 800, borderRadius: 999, px: 1.5 }}
                    >
                        {t("secondaryPanel.viewMarket")}
                    </Button>
                )}
            </Paper>
        );
    }

    const selectedPanelLabel = t(`investments.${selectedPanel}`);
    const formattedUpdatedAt = formatUpdatedAt(updatedAt, currentLanguage);
    const tableContent = isLoading || err || !displayRows.length
        ? undefined
        : (
            <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
                <QuotesTable
                    displayRows={displayRows}
                    onAddPosition={onAddPosition}
                    onOpenHistory={setHistoryTarget}
                    t={t}
                />
            </Box>
        );

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    borderRadius: 4,
                    bgcolor: "background.paper",
                    backgroundImage: (theme) => theme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.14)}, ${alpha(theme.palette.background.paper, 0.94)} 62%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.background.paper, 0.99)} 56%)`,
                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.14)}`,
                    boxShadow: (theme) => theme.palette.mode === "dark"
                        ? `0 16px 38px ${alpha(theme.palette.common.black, 0.24)}`
                        : `0 16px 34px ${alpha(theme.palette.common.black, 0.07)}`,
                }}
            >
                <Box sx={{ p: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
                    <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 950, fontSize: { xs: 24, sm: 28 }, lineHeight: 1.1 }}>
                                {t("investments.featuredAssets")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 700 }}>
                                {t("investments.featuredAssetsSubtitle", { panel: selectedPanelLabel })}
                            </Typography>
                        </Box>
                        <Tooltip
                            title={rateLimitRemaining > 0
                                ? `${t("investments.refreshQuotesWait")} (${rateLimitRemaining}s)`
                                : t("investments.refreshQuotes")}
                        >
                            <span>
                                <IconButton
                                    onClick={handleRefresh}
                                    disabled={rateLimitRemaining > 0}
                                    aria-label={t("investments.refreshQuotes")}
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        flex: "0 0 auto",
                                        color: "primary.main",
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.11 : 0.08),
                                        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.16)}`,
                                        "&:hover": {
                                            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.12),
                                        },
                                    }}
                                >
                                    <RefreshIcon sx={{ fontSize: 19 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>

                    {panelSelector}

                    {(isDualCurrency || formattedUpdatedAt) && (
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            justifyContent="space-between"
                            sx={{ mt: 1.35 }}
                        >
                            {isDualCurrency ? (
                                <ToggleButtonGroup
                                    value={quoteCurrencyView}
                                    exclusive
                                    onChange={(_, value: QuoteCurrency | null) => value && setQuoteCurrencyView(value)}
                                    size="small"
                                    sx={{
                                        p: 0.35,
                                        borderRadius: 999,
                                        alignSelf: { xs: "flex-start", sm: "center" },
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.06),
                                        "& .MuiToggleButton-root": {
                                            minHeight: 32,
                                            px: 1.4,
                                            border: 0,
                                            borderRadius: 999,
                                            fontSize: 12,
                                            fontWeight: 850,
                                            textTransform: "none",
                                        },
                                    }}
                                >
                                    <ToggleButton value="ARS">{t("investments.viewInPesos")}</ToggleButton>
                                    <ToggleButton value="USD">{t("investments.viewInDollars")}</ToggleButton>
                                </ToggleButtonGroup>
                            ) : <Box />}
                            {formattedUpdatedAt && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        fontWeight: 750,
                                        textAlign: { xs: "left", sm: "right" },
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    {t("investments.lastUpdate")}:{" "}
                                    <Box component="span" sx={{ color: "text.primary", fontWeight: 850 }}>
                                        {formattedUpdatedAt}
                                    </Box>
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Box>

                {isLoading ? (
                    <QuotesLoadingState />
                ) : err ? (
                    <Box sx={{ p: { xs: 2, sm: 2.5 }, pt: 1 }}>
                        <EmptyState icon={<ShowChartRoundedIcon />} title={t("investments.stocksError")} body={err} />
                    </Box>
                ) : displayRows.length === 0 ? (
                    <Box sx={{ p: { xs: 2, sm: 2.5 }, pt: 1 }}>
                        <EmptyState
                            icon={<ShowChartRoundedIcon />}
                            title={t("investments.noQuotesTitle")}
                            body={t("investments.noQuotesBody")}
                        />
                    </Box>
                ) : topMovers.length === 0 ? (
                    <Typography color="text.secondary" sx={{ px: { xs: 2, sm: 2.5 }, pb: 2.5, fontWeight: 700 }}>
                        {t("investments.noMovers")}
                    </Typography>
                ) : (
                    <Box>
                        {topMovers.map((row, index) => (
                            <Box
                                key={`${row.variant.ticker}-${index}`}
                                sx={{
                                    borderTop: index === 0
                                        ? (theme) => `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.06)}`
                                        : 0,
                                    borderBottom: index < topMovers.length - 1
                                        ? (theme) => `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.1 : 0.08)}`
                                        : 0,
                                }}
                            >
                                <QuoteMovementRow
                                    row={row}
                                    onAddPosition={onAddPosition}
                                    onOpenHistory={() => setHistoryTarget(row)}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
            </Paper>

            {displayRows.length > 0 && !isLoading && !err && (
                <ExpandableCard title={t("investments.allQuotes")}>{tableContent}</ExpandableCard>
            )}

            <OperationHistoryDialog
                open={Boolean(historyTarget)}
                symbol={historyTarget?.variant.ticker ?? "-"}
                operations={operationsForTicker}
                quoteCurrency={quoteCurrencyView}
                balancesVisible={balancesVisible}
                onClose={() => setHistoryTarget(null)}
            />
        </>
    );
}

function QuoteMovementRow({
    row,
    onAddPosition,
    onOpenHistory,
}: {
    row: DisplayRow;
    onAddPosition?: (params: AddPositionParams) => void;
    onOpenHistory: () => void;
}) {
    const { t } = useTranslation();
    const canAddPosition = Boolean(onAddPosition);
    const variation = row.variant.variation;
    const initials = row.variant.ticker.slice(0, 2).toUpperCase();

    const handleAddPosition = () => {
        if (!onAddPosition) return;
        onAddPosition({
            panel: row.panel,
            symbol: row.variant.ticker,
            name: row.quote.name,
            defaultPrice: row.variant.lastPrice ?? undefined,
        });
    };

    return (
        <Box
            role={canAddPosition ? "button" : undefined}
            tabIndex={canAddPosition ? 0 : undefined}
            onClick={handleAddPosition}
            onKeyDown={(event) => {
                if (!canAddPosition) return;
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleAddPosition();
                }
            }}
            sx={{
                p: { xs: 1.35, sm: 1.65 },
                display: "grid",
                gridTemplateColumns: { xs: "44px minmax(0, 1fr) minmax(88px, auto) 32px", sm: "52px minmax(0, 1fr) auto 36px" },
                gap: { xs: 1.1, sm: 1.35 },
                alignItems: "center",
                cursor: canAddPosition ? "pointer" : "default",
                "&:hover": canAddPosition ? {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.05),
                } : undefined,
            }}
        >
            <Box
                sx={{
                    width: { xs: 44, sm: 52 },
                    height: { xs: 44, sm: 52 },
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    color: variationColor(variation),
                    bgcolor: (theme) => alpha(variation && variation < 0 ? theme.palette.error.main : theme.palette.success.main, 0.12),
                    fontWeight: 950,
                    fontSize: { xs: 15, sm: 17 },
                }}
            >
                {initials}
            </Box>

            <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 950, fontSize: { xs: 16, sm: 17 }, lineHeight: 1.15 }}>
                    {row.variant.ticker}
                </Typography>
                <Typography noWrap variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {row.quote.name}
                </Typography>
                {canAddPosition && (
                    <Typography variant="caption" color="primary.main" sx={{ display: "block", mt: 0.35, fontWeight: 850 }}>
                        {t("investments.tapToRecord")}
                    </Typography>
                )}
            </Box>

            <Box sx={{ textAlign: "right", minWidth: { xs: 88, sm: 124 } }}>
                <Typography sx={{ fontWeight: 950, fontSize: { xs: 14, sm: 16 }, lineHeight: 1.15 }}>
                    {formatPrice(row.variant.lastPrice)}
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: variationColor(variation), fontWeight: 850 }}>
                    {variationLabel(variation)}
                </Typography>
            </Box>

            <IconButton
                size="small"
                aria-label={t("portfolio.viewHistory")}
                onClick={(event) => {
                    event.stopPropagation();
                    onOpenHistory();
                }}
                sx={{ color: "text.secondary" }}
            >
                <HistoryIcon fontSize="small" />
            </IconButton>
        </Box>
    );
}

function QuotesLoadingState() {
    return (
        <Box sx={{ p: { xs: 2, sm: 2.5 }, pt: 1 }} role="status" aria-live="polite" aria-busy="true">
            {[0, 1, 2].map((row) => (
                <Box key={row} sx={{ display: "grid", gridTemplateColumns: "44px minmax(0, 1fr) 84px", gap: 1.25, alignItems: "center", py: 1.25 }}>
                    <Skeleton variant="circular" width={44} height={44} />
                    <Box>
                        <Skeleton width="50%" height={24} />
                        <Skeleton width="72%" height={18} />
                    </Box>
                    <Skeleton width={76} height={26} />
                </Box>
            ))}
        </Box>
    );
}
